import React, { useState, useEffect } from 'react';

import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import ProductSelect from '@components/shared/products/ProductSelect';
import useSmartBack from '@hooks/shared/useSmartBack';
import { Form, Input, Button, Col, message, Row, Spin, Table, Typography, InputNumber, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

/**
 * ProductionBomEditView - 通用配方編輯組件
 *
 * @param {Object} props
 * @param {Object} props.productionBomService - 配方服務 (admin/operator productionBomService)
 * @param {Object} props.productService - 產品服務 (用於搜尋產品)
 * @param {Array} props.inventoryTypes - 庫存類型選項列表（從 Provider/wrapper 傳入，已依 merchant 過濾）
 * @param {string} props.role - 角色 (admin 或 operator)
 * @param {string} props.merchantId - Merchant ID (Operator 新增時必須；edit 時由 wrapper 對齊到 record 所屬 merchant)
 * @param {Function} [props.onProductionBomLoaded] - edit 模式 loadProductionBom 完成後回呼，
 *                   wrapper 用來把 OperatorDataContext.currentMerchant 對齊到 record 的 merchant。
 *                   shared 元件本身保持 merchant-agnostic，不直接讀 / 寫 merchant context。
 * @param {boolean} [props.merchantDataLoading] - wrapper 正在切換 context 到 record 的 merchant 時為 true，
 *                   render 期間整個 form 透過 Spin 擋住，避免閃過之前 merchant 的下拉選項。
 */
export default function ProductionBomEditView({
  productionBomService,
  productService,
  inventoryTypes = [],
  role = 'admin',
  merchantId = null,
  onProductionBomLoaded,
  merchantDataLoading = false,
}) {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);

  // 配方原料項目
  const [productionBomItems, setProductionBomItems] = useState([]);
  // 已載入的產出商品 option — 提供給 ProductSelect 作為 defaultOptions，
  // 否則初始載入時 antd Select 在 options 內找不到 value 會 fallback 顯示原始 UUID
  const [outputProductOption, setOutputProductOption] = useState(null);

  // 載入資料（編輯模式）
  useEffect(() => {
    if (id) {
      loadProductionBom();
    }
  }, [id]);

  // useProductSearch 用 `${sku} - ${name}` 當 label，這裡沿用同樣格式避免閉合狀態下顯示差異
  const buildProductOption = (product) => {
    if (!product?.id) return null;
    return {
      value: product.id,
      label: `${product.sku} - ${product.name}`,
      sku: product.sku,
      name: product.name,
    };
  };

  const loadProductionBom = async () => {
    try {
      setLoading(true);
      const response = await productionBomService.get(id);
      if (response.success) {
        const productionBom = response.data;
        // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
        // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。shared 本身
        // 不再讀 / 寫 merchant context，保持 merchant-agnostic。
        onProductionBomLoaded?.(productionBom);
        form.setFieldsValue({
          name: productionBom.name,
          output_product_id: productionBom.output_product_id,
          output_inventory_type_id: productionBom.output_inventory_type_id,
        });
        setOutputProductOption(buildProductOption(productionBom.output_product));

        // 設定配方原料
        if (productionBom.production_bom_items && productionBom.production_bom_items.length > 0) {
          setProductionBomItems(productionBom.production_bom_items.map((item, index) => ({
            key: index,
            input_product_id: item.input_product_id,
            input_product_name: item.input_product?.name,
            input_product_sku: item.input_product?.sku,
            quantity: item.quantity,
            input_inventory_type_id: item.input_inventory_type_id,
          })));
        }
      } else {
        message.error(response.message || t(`shared.production_boms.form.load_error`));
      }
    } catch (error) {
      console.error('Load production bom error:', error);
      message.error(t(`shared.production_boms.form.load_error`));
    } finally {
      setLoading(false);
    }
  };

  // 新增原料項目
  const handleAddItem = () => {
    const newKey = productionBomItems.length > 0
      ? Math.max(...productionBomItems.map(item => item.key)) + 1
      : 0;
    setProductionBomItems([...productionBomItems, {
      key: newKey,
      input_product_id: null,
      input_product_name: '',
      input_product_sku: '',
      quantity: 1,
      input_inventory_type_id: null
    }]);
  };

  // 刪除原料項目
  const handleRemoveItem = (key) => {
    setProductionBomItems(productionBomItems.filter(item => item.key !== key));
  };

  // 更新原料項目的產品
  const handleProductSelect = (key, productId, option) => {
    setProductionBomItems(productionBomItems.map(item =>
      item.key === key
        ? { 
            ...item, 
            input_product_id: productId, 
            input_product_name: option?.name, 
            input_product_sku: option?.sku 
          }
        : item
    ));
  };

  // 更新原料項目的數量
  const handleQuantityChange = (key, quantity) => {
    setProductionBomItems(productionBomItems.map(item =>
      item.key === key ? { ...item, quantity } : item
    ));
  };

  // 更新原料項目的庫存類型
  const handleInventoryTypeChange = (key, input_inventory_type_id) => {
    setProductionBomItems(productionBomItems.map(item =>
      item.key === key ? { ...item, input_inventory_type_id } : item
    ));
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  // pre-flight 驗證以 __aborted sentinel 統一處理（避免兩個 handler 重複驗證程式碼）
  const submitValues = async (values) => {
    // 驗證至少有一個原料
    if (productionBomItems.length === 0) {
      message.error(t(`shared.production_boms.form.no_items_error`));
      return { __aborted: true };
    }

    // 驗證所有原料都已選擇
    const hasEmptyItem = productionBomItems.some(item => !item.input_product_id);
    if (hasEmptyItem) {
      message.error(t(`shared.production_boms.form.incomplete_items_error`));
      return { __aborted: true };
    }

    const processedValues = {
      ...values,
      production_bom_items: productionBomItems.map(item => ({
        input_product_id: item.input_product_id,
        quantity: item.quantity,
        input_inventory_type_id: item.input_inventory_type_id,
      })),
    };

    // Operator 新增時需要加入 merchant_id
    if (role === 'operator' && isNew && merchantId) {
      processedValues.merchant_id = merchantId;
    }

    return isNew
      ? await productionBomService.create(processedValues)
      : await productionBomService.update(id, processedValues);
  };

  // 主要提交：成功後導向 show 頁
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await submitValues(values);
      if (response.__aborted) return;

      if (response.success) {
        message.success(t(`shared.production_boms.form.${isNew ? 'create' : 'update'}_success`));
        // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到新資料
        // React Router basename 會自動補上 /operator 或 /admin 前綴
        const recordId = response.data?.id || id;
        navigate(`/production_boms/${recordId}`, { replace: true });
      } else {
        message.error(response.message || t(`shared.production_boms.form.${isNew ? 'create' : 'update'}_error`));
      }
    } catch (error) {
      console.error('Submit error:', error);
      message.error(t(`shared.production_boms.form.${isNew ? 'create' : 'update'}_error`));
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);
        if (response.__aborted) return;

        if (response.success) {
          message.success(t(`shared.production_boms.form.create_success`));
          // 重置 antd form + 配方原料 useState（不在 form 內所以需手動重置）
          form.resetFields();
          setProductionBomItems([]);
        } else {
          message.error(response.message || t(`shared.production_boms.form.create_error`));
        }
      } catch (error) {
        console.error('Submit error:', error);
        message.error(t(`shared.production_boms.form.create_error`));
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  // 配方原料表格欄位
  const columns = [
    {
      title: t(`shared.production_boms.form.input_product`),
      dataIndex: 'input_product_id',
      key: 'product',
      width: '40%',
      render: (input_product_id, record) => {
        const currentOption = buildProductOption({
          id: record.input_product_id,
          sku: record.input_product_sku,
          name: record.input_product_name,
        });
        return (
          <ProductSelect
            productService={productService}
            merchantId={merchantId}
            value={input_product_id}
            placeholder={t(`shared.production_boms.form.select_product`)}
            style={{ width: '100%' }}
            onSelect={(val, option) => handleProductSelect(record.key, val, option)}
            defaultOptions={currentOption ? [currentOption] : []}
          />
        );
      },
    },
    {
      title: t(`shared.production_boms.form.input_inventory_type`),
      dataIndex: 'input_inventory_type_id',
      key: 'inventory_type',
      width: '25%',
      render: (input_inventory_type_id, record) => (
        <Select
          showSearch
          allowClear
          value={input_inventory_type_id}
          placeholder={t(`shared.production_boms.form.default_inventory_type_placeholder`)}
          style={{ width: '100%' }}
          onChange={(value) => handleInventoryTypeChange(record.key, value)}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={[
            { value: null, label: t(`shared.production_boms.form.default_inventory_type`) },
            ...inventoryTypes.map(type => ({
              value: type.id,
              label: type.name
            }))
          ]}
        />
      ),
    },
    {
      title: t(`shared.production_boms.form.quantity`),
      dataIndex: 'quantity',
      key: 'quantity',
      width: '20%',
      render: (quantity, record) => (
        <InputNumber
          min={1}
          value={quantity}
          onChange={(value) => handleQuantityChange(record.key, value)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('common.table.actions'),
      key: 'actions',
      width: '15%',
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(record.key)}
        >
          {t('common.button.delete')}
        </Button>
      ),
    },
  ];

  // Render-gate: sync 期間 product 下拉 / 子件選擇器不能用 header merchant 的清單 render。
  if (loading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label={t(`shared.production_boms.form.name`)}
                rules={[{ required: true, message: t('common.validation.required') }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="output_product_id"
                label={t(`shared.production_boms.form.output_product`)}
                rules={[{ required: true, message: t('common.validation.required') }]}
              >
                <ProductSelect
                  productService={productService}
                  merchantId={merchantId}
                  placeholder={t(`shared.production_boms.form.select_product`)}
                  defaultOptions={outputProductOption ? [outputProductOption] : []}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="output_inventory_type_id"
                label={t(`shared.production_boms.form.output_inventory_type`)}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder={t(`shared.production_boms.form.default_inventory_type_placeholder`)}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={[
                    { value: null, label: t(`shared.production_boms.form.default_inventory_type`) },
                    ...inventoryTypes.map(type => ({
                      value: type.id,
                      label: type.name
                    }))
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 配方原料 */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Typography.Title level={5} style={{ margin: 0 }}>
                {t(`shared.production_boms.form.materials`)}
              </Typography.Title>
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={handleAddItem}
              >
                {t(`common.button.add`)}
              </Button>
            </div>
            <Table
              columns={columns}
              dataSource={productionBomItems}
              rowKey="key"
              pagination={false}
              locale={{
                emptyText: (
                  <div style={{ padding: '24px 0' }}>
                    <p style={{ color: '#999', marginBottom: 12 }}>
                      {t(`shared.production_boms.detail.no_materials`)}
                    </p>
                    <Button icon={<PlusOutlined />} onClick={handleAddItem}>
                      {t(`shared.production_boms.form.add_material`)}
                    </Button>
                  </div>
                ),
              }}
            />
          </div>

          <FloatingActionBarSpacer />
        </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={savingAndAdding}
        extraButtons={isNew ? (
          <Button
            loading={savingAndAdding}
            disabled={loading}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        ) : null}
      />
    </>
  );
}
